import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '@/lib/supabase';

interface Comment {
  id: string;
  post_id: string;
  parent_id: string | null;
  author_name: string;
  content: string;
  is_anonymous: boolean;
  created_at: string;
  replies?: Comment[];
}

interface CommentSectionProps {
  postId: string;
}

export default function CommentSection({ postId }: CommentSectionProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author_name: '', content: '', is_anonymous: false });
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchComments = async () => {
    const { data, error } = await supabase
      .from('blog_comments')
      .select('*')
      .eq('post_id', postId)
      .eq('is_approved', true)
      .order('created_at', { ascending: true });

    if (!error && data) {
      const topLevel = data.filter((c: Comment) => !c.parent_id);
      const replies = data.filter((c: Comment) => c.parent_id);
      topLevel.forEach((c: Comment) => {
        c.replies = replies.filter((r: Comment) => r.parent_id === c.id);
      });
      setComments(topLevel);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.content.trim()) return;

    setSubmitting(true);
    setMessage(null);

    const { error } = await supabase.from('blog_comments').insert({
      post_id: postId,
      parent_id: replyTo?.id || null,
      author_name: form.is_anonymous ? 'Anonymous' : (form.author_name.trim() || 'Anonymous'),
      content: form.content,
      is_anonymous: form.is_anonymous,
    });

    setSubmitting(false);

    if (error) {
      setMessage({ type: 'error', text: 'Failed to post comment. Try again.' });
    } else {
      setForm({ author_name: '', content: '', is_anonymous: false });
      setReplyTo(null);
      setMessage({ type: 'success', text: 'Comment posted!' });
      fetchComments();
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const renderComment = (comment: Comment, isReply = false) => (
    <div key={comment.id} className={`${isReply ? 'ml-10 mt-3' : ''}`}>
      <div className={`p-4 rounded-lg ${isReply ? 'bg-background-50 border border-background-200' : 'bg-background-50 border border-background-200'}`}>
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">
              {(comment.author_name || 'A')[0].toUpperCase()}
            </span>
          </div>
          <div>
            <span className="text-sm font-semibold text-foreground-900">
              {comment.is_anonymous ? 'Anonymous' : (comment.author_name || 'Anonymous')}
            </span>
            <span className="text-xs text-foreground-500 ml-2">{formatDate(comment.created_at)}</span>
          </div>
        </div>
        <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
        <button
          onClick={() => setReplyTo({ id: comment.id, name: comment.is_anonymous ? 'Anonymous' : (comment.author_name || 'Anonymous') })}
          className="mt-3 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
        >
          <i className="ri-reply-line"></i>
          Reply
        </button>
      </div>
      {comment.replies?.map((reply) => renderComment(reply, true))}
    </div>
  );

  return (
    <section aria-labelledby="comments-heading" className="mt-12">
      <h2 id="comments-heading" className="text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
        <i className="ri-chat-3-line text-primary-500"></i>
        Comments {comments.length > 0 && <span className="text-sm font-normal text-foreground-500">({comments.length})</span>}
      </h2>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200 rounded-xl p-5 mb-8">
        {replyTo && (
          <div className="flex items-center justify-between mb-4 p-3 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary-700">
              Replying to <strong>{replyTo.name}</strong>
            </p>
            <button type="button" onClick={() => setReplyTo(null)} className="text-primary-500 hover:text-primary-700 cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="comment-name" className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">
                Name (optional)
              </label>
              <input
                id="comment-name"
                type="text"
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                placeholder="Your name"
                disabled={form.is_anonymous}
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 text-sm outline-none transition disabled:opacity-50"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_anonymous}
                  onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked, author_name: e.target.checked ? '' : form.author_name })}
                  className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                />
                <span className="text-sm text-foreground-600 whitespace-nowrap">Post anonymously</span>
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="comment-content" className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">
              Comment *
            </label>
            <textarea
              id="comment-content"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              required
              maxLength={500}
              rows={4}
              placeholder="Share your thoughts..."
              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 text-sm outline-none transition resize-none"
            ></textarea>
            <p className="text-xs text-foreground-500 mt-1">{form.content.length}/500 characters</p>
          </div>

          <button
            type="submit"
            disabled={submitting || !form.content.trim()}
            className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {submitting ? 'Posting...' : replyTo ? 'Post Reply' : 'Post Comment'}
          </button>
        </div>

        {message && (
          <div className={`mt-4 p-3 rounded-lg flex items-center gap-2.5 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-accent-100 border border-accent-200'
          }`}>
            <i className={`${message.type === 'success' ? 'ri-check-line text-green-600' : 'ri-error-warning-line text-accent-600'}`}></i>
            <p className="text-sm">{message.text}</p>
          </div>
        )}
      </form>

      {/* Comments List */}
      {loading ? (
        <p className="text-sm text-foreground-500">Loading comments...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-foreground-500 bg-background-50 rounded-lg p-4">No comments yet. Be the first to share your thoughts!</p>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => renderComment(comment))}
        </div>
      )}
    </section>
  );
}